'use client'
import React, { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { CMSLink } from '@/components/Link'
import RichText from '@/components/RichText'
import { cn } from '@/utilities/ui'

interface StickyCtaProps {
  stickyCta?: {
    title?: any
    description?: any
    showPhoneButton?: boolean
    ctaLinks?: Array<{
      link: {
        type?: 'reference' | 'custom'
        label?: string
        url?: string
        reference?: {
          value: any
          relationTo: 'pages' | 'posts'
        }
        newTab?: boolean
      }
      appearance?: 'default' | 'destructive' | 'ghost' | 'outline'
    }>
    position?: 'bottom' | 'top'
    showCloseButton?: boolean
    showOnDesktop?: boolean // Added this property
  }
}

export const StickyCta: React.FC<StickyCtaProps> = ({ stickyCta }) => {
  const [isVisible, setIsVisible] = useState(true)
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    // Slightly defer mount to avoid competing with initial rendering/paint
    const t = setTimeout(() => setIsMounted(true), 150)
    // Always show on page reload - don't check localStorage
    return () => clearTimeout(t)
  }, [])

  const handleClose = () => {
    setIsVisible(false)
    // Don't persist close state - it will show again on page reload
  }

  // Static phone number
  const STATIC_PHONE_NUMBER = '(940) 365-9999'
  const handlePhoneClick = () => {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    )
    if (isMobile) {
      // Remove non-numeric characters for tel: link
      const telNumber = STATIC_PHONE_NUMBER.replace(/[^\d]/g, '')
      window.location.href = `tel:${telNumber}`
    } else {
      alert(`Call Now for a Free Estimate: ${STATIC_PHONE_NUMBER}`)
    }
  }

  if (
    !isMounted ||
    !isVisible ||
    !stickyCta ||
    (window.innerWidth >= 1024 && !stickyCta.showOnDesktop)
  ) {
    return null
  }

  const {
    title,
    description,
    showPhoneButton = true,
    ctaLinks,
    position = 'bottom',
    showCloseButton = true,
  } = stickyCta

  const positionClasses = position === 'top' ? 'top-0' : 'bottom-0'

  return (
    <div
      className={cn(
        'fixed left-0 right-0 z-50 shadow-lg transition-transform duration-300 bg-primary text-primary-foreground',
        positionClasses,
        'transform translate-y-0',
      )}
      role="banner"
      aria-label="Sticky Call to Action"
    >
      <div className="container mx-auto px-4 py-1 md:py-4 relative">
        <div className="flex flex-col items-center justify-center md:flex-row md:justify-center md:items-center">
          {/* Content */}
          <div className="flex-1 text-center md:flex-none">
            {title && (
              <div className="font-semibold text-sm mb-1">
                <RichText data={title} enableGutter={false} enableProse={false} />
              </div>
            )}
            {description && (
              <div className="text-xs opacity-90">
                <RichText data={description} enableGutter={false} enableProse={false} />
              </div>
            )}
          </div>

          {/* Static Phone CTA Button */}
          {showPhoneButton && (
            <div className="flex items-center mt-2 md:mt-0 md:mx-4">
              <button
                onClick={handlePhoneClick}
                className={cn(
                  'inline-flex items-center font-semibold text-sm px-6 py-3 border border-primary rounded-full transition-colors duration-300 tracking-wide',
                  'text-primary bg-primary-foreground hover:bg-primary/90',
                )}
                aria-label={`Call ${STATIC_PHONE_NUMBER}`}
                type="button"
              >
                Call Now for a Free Estimate
              </button>
            </div>
          )}

          {/* CTA Links */}
          {ctaLinks && ctaLinks.length > 0 && (
            <div className="flex flex-wrap gap-1 md:gap-2 justify-center mt-2 md:mt-0">
              {ctaLinks.map((item, index) => (
                <CMSLink
                  key={index}
                  {...item.link}
                  className={cn(
                    'inline-flex items-center px-6 py-8 text-base font-semibold border border-primary rounded-full transition-colors duration-300 tracking-wide',
                    'text-primary bg-primary-foreground hover:bg-primary/90',
                  )}
                />
              ))}
            </div>
          )}

          {/* Close Button */}
          {showCloseButton && (
            <button
              onClick={handleClose}
              className={cn(
                'absolute top-1 right-1',
                'p-1 rounded-full hover:bg-primary-foreground/10 transition-colors duration-200',
              )}
              aria-label="Close sticky CTA"
            >
              <X className="w-3 h-3 md:w-4 md:h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
